import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Inbox, MailPlus, Pencil, Trash2, Undo2 } from 'lucide-react';
import {
  deleteCampaign, freigabeSchritt, getCampaign, listCampaignHistory, listMyCampaigns, listSegments,
  type Campaign,
} from '../../lib/supabase';
import { STAND_META, standVon, type Stand } from '../../lib/freigabe';
import { vorlage } from '../../lib/mailBuilder/vorlagen';
import { formatDate, formatDateTime } from '../../lib/format';
import { editorPfad, istTeam } from '../../lib/rollen';
import { useAuthStore } from '../../stores/authStore';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { FreigabeBadge } from '../../components/Freigabe';
import { MailDetail } from '../../components/MailDetail/MailDetail';
import { useToast } from '../../components/Toast';
import './MeineMails.css';

type Filter = 'alle' | 'mir' | 'redaktion' | 'erledigt';

const FILTER: { id: Filter; label: string; staende: Stand[] | null }[] = [
  { id: 'alle', label: 'Alle', staende: null },
  { id: 'mir', label: 'Bei mir', staende: ['entwurf', 'rueckfrage'] },
  { id: 'redaktion', label: 'Bei der Redaktion', staende: ['eingereicht', 'in_pruefung', 'freigegeben'] },
  { id: 'erledigt', label: 'Erledigt', staende: ['geplant', 'wird_versendet', 'versendet', 'abgelehnt', 'fehler', 'archiviert'] },
];

export function MeineMails() {
  const [params, setParams] = useSearchParams();
  const gewaehltId = params.get('mail');
  const [filter, setFilter] = useState<Filter>('alle');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);

  const liste = useQuery({
    queryKey: ['my-campaigns'],
    queryFn: listMyCampaigns,
    refetchInterval: (q) => ((q.state.data as Campaign[] | undefined)?.some((c) => c.status === 'sending') ? 5000 : false),
  });
  const detail = useQuery({ queryKey: ['campaign', gewaehltId], queryFn: () => getCampaign(gewaehltId!), enabled: !!gewaehltId });
  const verlauf = useQuery({ queryKey: ['campaign-history', gewaehltId], queryFn: () => listCampaignHistory(gewaehltId!), enabled: !!gewaehltId });
  const gruppen = useQuery({ queryKey: ['segments'], queryFn: listSegments });

  const mails = useMemo(() => liste.data ?? [], [liste.data]);
  const zahl = (f: (typeof FILTER)[number]) => (f.staende ? mails.filter((c) => f.staende!.includes(standVon(c))).length : mails.length);
  const sichtbar = useMemo(() => {
    const f = FILTER.find((x) => x.id === filter)!;
    return f.staende ? mails.filter((c) => f.staende!.includes(standVon(c))) : mails;
  }, [mails, filter]);

  const oeffnen = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('mail', id); else p.delete('mail');
    setParams(p, { replace: !id });
  };

  const neuLaden = (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['my-campaigns'] });
    if (id) {
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
      void qc.invalidateQueries({ queryKey: ['campaign-history', id] });
    }
  };

  const zurueckziehen = useMutation({
    mutationFn: (c: Campaign) => freigabeSchritt(c.id, 'zurueckziehen', null, c.review_state ?? c.status),
    onSuccess: (_d, c) => {
      neuLaden(c.id);
      toast.success('Zurückgezogen — Du kannst die Mail jetzt ändern und neu einreichen.');
      navigate(`/neue-mail/${c.id}`);
    },
    onError: (e) => { toast.error((e as Error).message); neuLaden(gewaehltId ?? undefined); },
  });

  const loeschen = useMutation({
    mutationFn: (c: Campaign) => deleteCampaign(c.id),
    onSuccess: () => { neuLaden(); oeffnen(null); toast.success('Entwurf gelöscht.'); },
    onError: (e) => toast.error('Löschen hat nicht geklappt: ' + (e as Error).message),
  });

  const c = detail.data ?? null;
  const stand = c ? standVon(c) : null;
  const bearbeitenPfad = c ? (c.builder_template ? `/neue-mail/${c.id}` : editorPfad(user, c.id)) : '#';
  // Mails aus dem Event Manager ändern Geschäftsführung und Studioleitung nicht selbst,
  // sie schlagen Änderungen vor (OG73).
  const nurVorschlagen = !!c && !c.builder_template && !istTeam(user);

  const aktionen = c && stand ? (
    <>
      {(stand === 'entwurf' || stand === 'rueckfrage') && (
        <Link to={bearbeitenPfad} className="btn btn-primary btn-sm"><Pencil size={14} aria-hidden="true" /> {nurVorschlagen ? 'Ansehen und Änderungen vorschlagen' : stand === 'rueckfrage' ? 'Anpassen und erneut einreichen' : 'Weiter bearbeiten'}</Link>
      )}
      {(stand === 'eingereicht' || stand === 'in_pruefung') && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={zurueckziehen.isPending}
          onClick={() => {
            if (confirm('Die Mail geht zurück in Deine Entwürfe. Danach änderst Du sie und reichst sie neu ein. Zurückziehen?')) zurueckziehen.mutate(c);
          }}
        >
          <Undo2 size={14} aria-hidden="true" /> Zurückziehen und ändern
        </button>
      )}
      {stand === 'entwurf' && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ color: 'var(--danger-dark)' }}
          disabled={loeschen.isPending}
          onClick={() => { if (confirm(`Entwurf „${c.original_subject || c.subject || c.name}" löschen?`)) loeschen.mutate(c); }}
        >
          <Trash2 size={14} aria-hidden="true" /> Löschen
        </button>
      )}
      {!['entwurf', 'rueckfrage', 'eingereicht', 'in_pruefung'].includes(stand) && (
        <span className="mm-aktionen-text">{STAND_META[stand].text}</span>
      )}
    </>
  ) : null;

  return (
    <div>
      <PageHeader
        title="Meine Mails"
        subtitle="Alles, was Du vorbereitet hast — und wo es gerade steht."
        actions={<Link to="/neue-mail" className="btn btn-primary"><MailPlus size={16} aria-hidden="true" /> Neue Mail</Link>}
      />

      <div className="seg-control mm-filter" role="group" aria-label="Nach Stand filtern" data-tour="meine-filter">
        {FILTER.map((f) => (
          <button key={f.id} type="button" className={`seg-btn${filter === f.id ? ' active' : ''}`} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label} <span className="mm-zahl">{zahl(f)}</span>
          </button>
        ))}
      </div>

      {liste.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : mails.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Noch keine Mails"
          description={"Mit „Neue Mail“ startest Du den Assistenten: Vorlage wählen, ein paar Fragen beantworten, Felder ausfüllen — fertig."}
          action={<Link to="/neue-mail" className="btn btn-primary"><MailPlus size={16} aria-hidden="true" /> Erste Mail vorbereiten</Link>}
        />
      ) : (
        <div className={`mm-layout${gewaehltId ? ' mm-layout--detail' : ''}`}>
          <div className="mm-liste" data-tour="meine-liste">
            {sichtbar.length === 0 && <p className="mm-leer">Hier ist gerade nichts.</p>}
            {sichtbar.map((m) => {
              const s = standVon(m);
              const v = vorlage(m.builder_template);
              return (
                <button key={m.id} type="button" className={`mm-eintrag${m.id === gewaehltId ? ' aktiv' : ''}`} onClick={() => oeffnen(m.id)} aria-current={m.id === gewaehltId ? 'true' : undefined}>
                  <span className="mm-eintrag-kopf">
                    <FreigabeBadge campaign={m} titel={false} />
                    {m.is_urgent && <span className="badge badge-danger">Eilt</span>}
                    <span className="mm-wer">{STAND_META[s].wer === 'Du' ? 'Du bist dran' : STAND_META[s].wer === 'Redaktion' ? 'Redaktion ist dran' : ''}</span>
                  </span>
                  <strong className="mm-betreff">{m.original_subject || m.subject || m.name}</strong>
                  <span className="mm-meta">
                    {v ? `${v.emoji} ${v.name}` : 'Kampagne'}
                    {m.target_studios?.length ? ` · ${m.target_studios.join(', ')}` : ''}
                    {m.sent_at ? ` · versendet ${formatDate(m.sent_at)}` : m.scheduled_at ? ` · geplant ${formatDateTime(m.scheduled_at)}` : m.desired_send_at ? ` · Wunsch ${formatDateTime(m.desired_send_at)}` : ` · geändert ${formatDate(m.updated_at)}`}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mm-detail" data-tour="meine-detail">
            {!gewaehltId ? (
              <div className="mm-platzhalter">Wähl links eine Mail — hier siehst Du dann Vorschau, Stand und Verlauf.</div>
            ) : detail.isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
            ) : !c ? (
              <div className="mm-platzhalter">Diese Mail gibt es nicht (mehr).</div>
            ) : (
              <>
                <button type="button" className="btn btn-ghost btn-sm mm-zurueck" onClick={() => oeffnen(null)}><ArrowLeft size={14} aria-hidden="true" /> Zur Liste</button>
                <MailDetail c={c} verlauf={verlauf.data ?? []} verlaufLaedt={verlauf.isLoading} gruppen={gruppen.data ?? []} sicht="autor" aktionen={aktionen} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
